import { useRef, useState } from "react";
import { ImageUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import api, { apiError } from "@/lib/api";

const BAD_LOGO = "Logo must be PNG, JPG, SVG or WEBP under 2 MB";
const OK_EXT = ["png", "jpg", "jpeg", "svg", "webp"];

export default function BrandingPage() {
  const { company, logoUrl, reload: reloadCompany } = useCompany();
  const { user, refreshUser } = useAuth();
  const reload = () => Promise.all([reloadCompany(), refreshUser?.()]);
  const [name, setName] = useState(company.name);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const input = useRef(null);

  const saveName = async () => {
    setBusy("name");
    try { await api.put("/company", { name }); await reload(); toast.success("Company name saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const upload = async (file) => {
    setError("");
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!OK_EXT.includes(ext) || file.size > 2 * 1024 * 1024) { setError(BAD_LOGO); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy("logo");
    try { await api.post("/company/logo", fd); await reload(); toast.success("Logo updated"); }
    catch (e) { setError(apiError(e)); } finally { setBusy(""); if (input.current) input.current.value = ""; }
  };

  const toggleHeader = async (k, v) => {
    setBusy(k);
    try { await api.put(`/admin/companies/${company.id}/header`, { header_logo: company.header_logo !== false, header_name: company.header_name !== false, [k]: v }); await reload(); toast.success("Header updated"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const toggleBg = async (v) => {
    setBusy("bg");
    try { await api.put("/company/logo/settings", { remove_bg: v }); await reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="branding-page">
      <PageHeader eyebrow="Company" title="Branding" subtitle="Your logo and name appear in this panel, on the public view and in every PDF and Excel export." />
      {user?.is_super_admin && (
        <section className="rise-in rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="header-settings">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">App header</h2>
          <p className="mt-1 text-sm text-slate-500">Only the super admin can change these. Exports and emails are not affected.</p>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-3 text-sm"><Switch checked={company.header_logo !== false} disabled={!!busy} onCheckedChange={(v) => toggleHeader("header_logo", v)} data-testid="header-logo-switch" /> Show logo in header</label>
            <label className="flex items-center gap-3 text-sm"><Switch checked={company.header_name !== false} disabled={!!busy} onCheckedChange={(v) => toggleHeader("header_name", v)} data-testid="header-name-switch" /> Show company name in header</label>
          </div>
        </section>
      )}
      <section className="rise-in rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">Logo</h2>
        <div className="mt-4 flex flex-wrap items-center gap-6">
          <div className="rounded-md bg-slate-900 p-3" title="Preview on the app's dark header">
            <BrandMark name={company.name} logoUrl={logoUrl} className="h-16 w-36" testid="branding-logo-preview" />
          </div>
          <div className="space-y-2">
            <input ref={input} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="logo-file-input" />
            <Button variant="outline" onClick={() => input.current?.click()} disabled={busy === "logo"} className="gap-1.5" data-testid="logo-upload-button">
              {busy === "logo" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />} {company.has_logo ? "Replace logo" : "Upload logo"}
            </Button>
            <p className="text-xs text-slate-500">PNG, JPG, SVG or WEBP · max 2 MB · shown without stretching</p>
            {company.has_logo && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={company.logo_remove_bg !== false} disabled={busy === "bg"} onCheckedChange={toggleBg} data-testid="logo-remove-bg-switch" /> Remove white background
              </label>
            )}
            {company.logo_artwork_dark && company.logo_remove_bg !== false && <p className="max-w-xs text-xs text-slate-500" data-testid="logo-dark-note">Your logo artwork is dark, so a subtle light outline keeps it readable on the dark header and PDF bar.</p>}
          </div>
        </div>
        {error && <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="logo-error">{error}</p>}
      </section>
      <section className="rise-in rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">Company name</h2>
        <div className="mt-4 flex max-w-md items-end gap-3">
          <div className="flex-1 space-y-1.5"><Label htmlFor="company-name">Name</Label><Input id="company-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="branding-name-input" /></div>
          <Button onClick={saveName} disabled={busy === "name" || name.trim().length < 2} className="bg-teal-600 hover:bg-teal-700" data-testid="branding-name-save">Save</Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">Public view address: <span className="font-mono">/view/{company.slug}</span></p>
      </section>
    </div>
  );
}
